/**
 * ProvisioningModal Component - AGENT-001
 *
 * Modal showing agent provisioning progress with WebSocket updates.
 * States: Pending, Running, Paused, Completed, Failed
 *
 * @module components/admin/ProvisioningModal
 */

import { useState, useEffect, useCallback, memo } from 'react';
import {
  Play,
  Loader2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  X,
  Minimize2
} from 'lucide-react';
import { useWebSocket } from '../../hooks/useWebSocket';

// ============================================
// Constants
// ============================================

/**
 * Provisioning step definitions matching backend
 */
const PROVISIONING_STEPS = [
  { name: 'user_check', description: 'Check if user exists' },
  { name: 'provision_script', description: 'Run provisioning script' },
  { name: 'identity_skill', description: 'Create identity skill' },
  { name: 'push_skills', description: 'Push skills to agent' },
  { name: 'credential_key', description: 'Deploy credential key' },
  { name: 'finalize', description: 'Finalize activation' }
];

/**
 * Status configuration
 */
const STATUS_CONFIG = {
  PENDING: {
    label: 'Starting...',
    icon: Loader2,
    iconClass: 'text-blue-400 animate-spin',
    bgClass: 'bg-blue-500/10'
  },
  RUNNING: {
    label: 'Provisioning',
    icon: Loader2,
    iconClass: 'text-blue-400 animate-spin',
    bgClass: 'bg-blue-500/10'
  },
  PAUSED: {
    label: 'Paused',
    icon: AlertTriangle,
    iconClass: 'text-amber-400',
    bgClass: 'bg-amber-500/10'
  },
  COMPLETED: {
    label: 'Completed',
    icon: CheckCircle,
    iconClass: 'text-green-400',
    bgClass: 'bg-green-500/10'
  },
  FAILED: {
    label: 'Failed',
    icon: XCircle,
    iconClass: 'text-red-400',
    bgClass: 'bg-red-500/10'
  },
  CANCELLED: {
    label: 'Cancelled',
    icon: XCircle,
    iconClass: 'text-gray-400',
    bgClass: 'bg-gray-500/10'
  }
};

// ============================================
// Sub-Components
// ============================================

/**
 * Progress step indicator
 */
const StepIndicator = memo(function StepIndicator({
  step,
  index,
  currentStep,
  status
}) {
  const isCompleted = index < currentStep;
  const isCurrent = index === currentStep && status === 'RUNNING';
  const isPending = index > currentStep || (index === currentStep && status === 'PENDING');
  const isFailed = index === currentStep && (status === 'FAILED' || status === 'PAUSED');

  let iconElement;
  let textClass = 'text-gray-500';

  if (isCompleted) {
    iconElement = <CheckCircle className="w-4 h-4 text-green-400" />;
    textClass = 'text-green-400';
  } else if (isCurrent) {
    iconElement = <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
    textClass = 'text-blue-400';
  } else if (isFailed) {
    iconElement = <XCircle className="w-4 h-4 text-red-400" />;
    textClass = 'text-red-400';
  } else if (isPending) {
    iconElement = (
      <div className="w-4 h-4 rounded-full border-2 border-gray-600 flex items-center justify-center">
        <span className="text-[10px] text-gray-500">{index + 1}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 py-1.5">
      {iconElement}
      <span className={`text-sm ${textClass}`}>
        {step.description}
      </span>
    </div>
  );
});

/**
 * Progress bar component
 */
const ProgressBar = memo(function ProgressBar({ current, total }) {
  const percentage = total > 0 ? Math.round((current / total) * 100) : 0;

  return (
    <div className="w-full">
      <div className="flex justify-between text-xs text-gray-400 mb-1">
        <span>Step {current} of {total}</span>
        <span>{percentage}%</span>
      </div>
      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
        <div
          className="h-full bg-blue-500 transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
});

// ============================================
// Main Component
// ============================================

/**
 * ProvisioningModal Component
 *
 * @param {object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {object} props.agent - Agent being provisioned
 * @param {string} props.jobId - Provisioning job ID
 * @param {function} props.onClose - Close handler
 * @param {function} [props.onRetry] - Retry handler
 * @param {function} [props.onComplete] - Completion handler
 * @returns {React.ReactElement|null} Modal element or null
 */
const ProvisioningModal = memo(function ProvisioningModal({
  isOpen,
  agent,
  jobId,
  onClose,
  onRetry,
  onComplete
}) {
  // ----------------------------------------
  // State
  // ----------------------------------------

  const [status, setStatus] = useState('PENDING');
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(PROVISIONING_STEPS.length);
  const [stepName, setStepName] = useState('');
  const [error, setError] = useState(null);
  const [canRetry, setCanRetry] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);

  const { subscribe } = useWebSocket();

  // ----------------------------------------
  // WebSocket Subscriptions
  // ----------------------------------------

  useEffect(() => {
    if (!isOpen || !agent?.id) return;

    // Subscribe to progress events
    const unsubProgress = subscribe('provisioning:progress', (data) => {
      if (data.agentId === agent.id) {
        setStatus(data.status);
        setCurrentStep(data.currentStep);
        setTotalSteps(data.totalSteps);
        setStepName(data.stepName);
        setError(null);
      }
    });

    // Subscribe to completion events
    const unsubComplete = subscribe('provisioning:complete', (data) => {
      if (data.agentId === agent.id) {
        setStatus('COMPLETED');
        setCurrentStep(totalSteps);
        onComplete?.(data.agent);
      }
    });

    // Subscribe to error events
    const unsubError = subscribe('provisioning:error', (data) => {
      if (data.agentId === agent.id) {
        setStatus(data.status);
        setError(data.error);
        setCanRetry(data.canRetry);
      }
    });

    return () => {
      unsubProgress?.();
      unsubComplete?.();
      unsubError?.();
    };
  }, [isOpen, agent?.id, subscribe, totalSteps, onComplete]);

  // ----------------------------------------
  // Handlers
  // ----------------------------------------

  const handleRetry = useCallback(async () => {
    if (!agent?.id || !onRetry) return;

    setIsRetrying(true);
    setError(null);

    try {
      await onRetry(agent);
      setStatus('PENDING');
    } catch (err) {
      setError(err.message || 'Retry failed');
    } finally {
      setIsRetrying(false);
    }
  }, [agent, onRetry]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleMinimize = useCallback(() => {
    // Allow user to continue in background - provisioning keeps running via cron worker
    onClose();
  }, [onClose]);

  // ----------------------------------------
  // Render
  // ----------------------------------------

  if (!isOpen) return null;

  const config = STATUS_CONFIG[status] || STATUS_CONFIG.PENDING;
  const StatusIcon = config.icon;
  const isRunning = status === 'RUNNING' || status === 'PENDING';
  const showRetry = (status === 'FAILED' || status === 'PAUSED') && canRetry && onRetry;
  const showClose = status === 'COMPLETED' || status === 'FAILED' || status === 'CANCELLED';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-700">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Play className="w-5 h-5 text-green-400" />
              Activating {agent?.displayName || agent?.name}
            </h3>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 text-gray-400 hover:text-white rounded transition-colors"
              title={isRunning ? "Continue in background" : "Close"}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Status Badge */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4 ${config.bgClass}`}>
            <StatusIcon className={`w-4 h-4 ${config.iconClass}`} />
            <span className="text-sm font-medium text-gray-200">{config.label}</span>
          </div>

          {/* Progress Bar */}
          <div className="mb-4">
            <ProgressBar current={currentStep} total={totalSteps} />
          </div>

          {/* Step List */}
          <div className="max-h-64 overflow-y-auto pr-2">
            {PROVISIONING_STEPS.map((step, index) => (
              <StepIndicator
                key={step.name}
                step={step}
                index={index}
                currentStep={currentStep}
                status={status}
              />
            ))}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* Success Message */}
          {status === 'COMPLETED' && (
            <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-sm text-green-400">
                Agent successfully activated and ready to use.
              </p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-6 py-4 border-t border-gray-700 flex justify-end gap-3">
          {showRetry && (
            <button
              type="button"
              onClick={handleRetry}
              disabled={isRetrying}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-500 rounded-lg transition-colors disabled:opacity-50"
            >
              {isRetrying ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Retry
            </button>
          )}
          {showClose && (
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              Close
            </button>
          )}
          {isRunning && (
            <button
              type="button"
              onClick={handleMinimize}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
            >
              <Minimize2 className="w-4 h-4" />
              Continue in Background
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

export default ProvisioningModal;
